import React, { useEffect, useRef, useState } from "react";
import { UpOutlined, DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Spin, Tag, Tooltip } from "antd";
import { Calendar, CircleCheck, CircleUnCheck, Watch, Tag as TagNotLinkedIcon } from "assets/icons";
import { VehicleTrailerType } from "store/vehicle-dashboard/vehicle-dashboard-model";
import theme from "styles/theme";
import { convertTimeSpanToTime } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./../index.styled";
import Informations, { IDataInformation } from "./Informations";
import TruckImage from "./TruckImage";

interface IPropsScreenVehicleDashboard {
  isLoadingRegisters?: boolean;
  registers?: Array<IRegisters>;
  description: string;
  totalRegisters: string;
  totalLengthOfStay?: string;
  vehicleCardInformation: IDataInformation[];
  hasReleased: boolean;
  hasRfidReadingActive?: boolean;
}

interface IDispatchProps {
  lobbyFetchItemCheckinAsync: (...args) => void;
  lobbyFetchItemAsync: (...args) => void;
  vehicleDashboardShowDrawerViewCheckinRegister: (...args) => void;
  vehicleDashboardShowDrawerViewRegister: (...args) => void;
}

interface IRegisters {
  id: number;
  saleChannelDescription?: string;
  saleChannelId?: number;
  vehiclePlate: string;
  scheduleDate?: string;
  lenghtOfStay?: string;
  local?: number;
  vehicleStepStatus?: number;
  process?: string;
  hasActiveTag: boolean;
  vehicleType?: number | null;
}

export type IVehicleDashboardProps = IPropsScreenVehicleDashboard & IDispatchProps;

const InfoTag = ({ colorStatus, colorText, description }) => (
  <Tag className="vehicleInfo" style={{ color: colorText }} color={colorStatus}>
    {description}
  </Tag>
);

const VehiclesCards = ({
  registers,
  description,
  hasReleased,
  totalRegisters,
  totalLengthOfStay,
  isLoadingRegisters,
  vehicleCardInformation,
  hasRfidReadingActive,
  lobbyFetchItemAsync,
  lobbyFetchItemCheckinAsync,
  vehicleDashboardShowDrawerViewRegister,
  vehicleDashboardShowDrawerViewCheckinRegister,
}: IVehicleDashboardProps) => {
  const THERE_ARE_NO_RECORDS = useTranslation({ id: "there_are_no_records" });
  const RELEASED_VEHICLE = useTranslation({ id: "released_vehicle" });
  const VEHICLE_NOT_RELEASED = useTranslation({ id: "vehicle_not_released" });
  const SEE_DETAILS = useTranslation({ id: "see_details" });
  const SHOW_HIDE = useTranslation({ id: "show_hide" });
  const VEHICLE_DOES_NOT_HAVE_A_TAG = useTranslation({ id: "attention_the_vehicle_does_not_have_a_registered_tag_give_a_new_one_to_the_driver" });
  const BOX_TRAILER = useTranslation({ id: "box_trailer" });
  const BULK_TRAILER = useTranslation({ id: "bulk_trailer" });
  const CURTAINSIDER = useTranslation({ id: "curtainsider" });
  const FLATBED = useTranslation({ id: "flatbed" });
  const LIGHTWEIGHT_CURTAINSIDER = useTranslation({ id: "lightweight_curtainsider" });
  const TANKER = useTranslation({ id: "tanker" });
  const OTHERS = useTranslation({ id: "fleet_channel_others" });
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const maneuverProcess = useTranslation({ id: "maneuver" });
  const ISNOTRELEASED = 2;
  const ISRELEASED = 3;

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  useEffect(() => {
    checkScroll();

    const el = scrollRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
    }

    return () => {
      if (el) {
        el.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      }
    };
  }, [isCollapsed, canScrollRight, canScrollLeft]);

  useEffect(() => {
    checkScroll();
  }, [registers]);

  const handleOpenDrawerVehicleDetails = (yardRegisterId, local) => {
    checkIfIsFeaturedOrInLocationToShowViewDrawer(
      local,
      yardRegisterId,
      lobbyFetchItemCheckinAsync,
      vehicleDashboardShowDrawerViewCheckinRegister,
      lobbyFetchItemAsync,
      vehicleDashboardShowDrawerViewRegister
    );
  };

  const checkIfIsFeaturedOrInLocationToShowViewDrawer = (
    eventType,
    yardRegisterId,
    lobbyFetchItemCheckinAsync,
    vehicleDashboardShowDrawerViewCheckinRegister,
    lobbyFetchItemAsync,
    vehicleDashboardShowDrawerViewRegister
  ) => {
    const isCheckinOrInRadiusData = [0, 4];
    if (isCheckinOrInRadiusData.includes(eventType)) {
      lobbyFetchItemCheckinAsync(yardRegisterId);
      vehicleDashboardShowDrawerViewCheckinRegister();
    } else {
      lobbyFetchItemAsync(yardRegisterId);
      vehicleDashboardShowDrawerViewRegister();
    }
  };

  const scheduling = ["Chegando", "No Raio", "No raio sem check-in", "Fila interna", "Liberado / Não liberado"];
  const lenghtOfStay = ["Fila interna", "Em operação"];
  const statusAcceptedByManeuverProcess = [
    { id: 8, description: "InRadius" },
    { id: 5, description: "InternQueue" },
    { id: 6, description: "InOperation" },
    { id: 2, description: "NotReleased" },
    { id: 3, description: "Released" },
  ];
  const vehicleTypeDescriptionById: Record<number, string> = {
    [VehicleTrailerType.BOX_TRAILER]: BOX_TRAILER,
    [VehicleTrailerType.CURTAINSIDER_DELTA_OPENING]: CURTAINSIDER,
    [VehicleTrailerType.FLATBED]: FLATBED,
    [VehicleTrailerType.LIGHTWEIGHT_CURTAINSIDER]: LIGHTWEIGHT_CURTAINSIDER,
    [VehicleTrailerType.BULK_TRAILER]: BULK_TRAILER,
    [VehicleTrailerType.TANKER]: TANKER,
    [VehicleTrailerType.OTHER]: OTHERS,
  };

  const getVehicleTypeDescription = (vehicleType?: number | null) => {
    if (vehicleType == null) return OTHERS;
    return vehicleTypeDescriptionById[vehicleType] ?? OTHERS;
  };

  const isManeuverRegister = (register: IRegisters) =>
    register.process === maneuverProcess && statusAcceptedByManeuverProcess.some((status) => status.id === register.vehicleStepStatus);

  return (
    <S.CardContent>
      <S.HeaderContainer>
        <S.HeaderDescription>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div>
              <Informations informations={vehicleCardInformation} />
            </div>
            <S.CardTitle>{description}</S.CardTitle>
            <Tag className="vehicleInfoDescription" style={{ color: "#fff", fontSize: "18px" }} color="#666">
              {totalRegisters || 0}
            </Tag>
            {totalLengthOfStay && (
              <div style={{ display: "flex", gap: 5, justifyItems: "center", alignItems: "center" }}>
                <Watch width={25} height={25} />
                <S.TotalLengthOfStay>{totalLengthOfStay}</S.TotalLengthOfStay>
              </div>
            )}
          </div>
          <button
            className="collapse-icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            type="button"
            style={{ background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
            aria-label={SHOW_HIDE}
          >
            {isCollapsed ? <DownOutlined /> : <UpOutlined />}
          </button>
        </S.HeaderDescription>
        <div className="divider" />
      </S.HeaderContainer>
      {!isCollapsed && (
        <S.ScrollWrapper hasSchedule={scheduling.includes(description)} hasLenghtOfStay={lenghtOfStay.includes(description)}>
          {canScrollLeft && (
            <S.ArrowButton onClick={scrollLeft} left>
              <LeftOutlined style={{ fontSize: 14 }} />
            </S.ArrowButton>
          )}
          <div style={isLoadingRegisters ? S.LoadingContainer : null}>
            {isLoadingRegisters ? (
              <Spin />
            ) : (
              <S.ItemsContainer ref={scrollRef} hasSchedule={scheduling.includes(description)} hasLenghtOfStay={lenghtOfStay.includes(description)}>
                {registers?.length > 0 ? (
                  registers?.map((register) => (
                    <S.TruckContainer key={register.id}>
                      <S.TruckButton onClick={() => handleOpenDrawerVehicleDetails(register.id, register.local)} data-testid="truckImage" type="button" aria-label={SEE_DETAILS}>
                        <Tooltip placement="top" title={isManeuverRegister(register) ? maneuverProcess : getVehicleTypeDescription(register.vehicleType)}>
                          <div>
                            <TruckImage saleChannelId={register.saleChannelId} vehicleType={register.vehicleType} isManeuver={isManeuverRegister(register)} />
                          </div>
                        </Tooltip>
                        <div>
                          {hasReleased &&
                            typeof register.vehicleStepStatus === "number" &&
                            (register.vehicleStepStatus === ISRELEASED ? (
                              <Tooltip placement="top" title={RELEASED_VEHICLE}>
                                <CircleCheck data-testid="released" style={S.ReleasedIcon} />
                              </Tooltip>
                            ) : register.vehicleStepStatus === ISNOTRELEASED ? (
                              <Tooltip placement="top" title={VEHICLE_NOT_RELEASED}>
                                <CircleUnCheck data-testid="notreleased" style={S.ReleasedIcon} />
                              </Tooltip>
                            ) : null)}
                          {hasRfidReadingActive && !register.hasActiveTag && typeof register.hasActiveTag === "boolean" && (
                            <Tooltip placement="top" title={VEHICLE_DOES_NOT_HAVE_A_TAG}>
                              <TagNotLinkedIcon data-testid="noTag" style={S.TagNotLinkedIcon} />
                            </Tooltip>
                          )}
                        </div>
                      </S.TruckButton>
                      <S.InfoTags hasSaleChannel={register.saleChannelDescription}>
                        {register.saleChannelDescription && (
                          <InfoTag description={register.saleChannelDescription} colorStatus={theme["@muted-background"]} colorText={theme["@profile-text-color"]} />
                        )}
                        <InfoTag description={register.vehiclePlate.toUpperCase()} colorStatus={theme["@muted-background"]} colorText={theme["@profile-text-color"]} />
                      </S.InfoTags>
                      {register?.scheduleDate && (
                        <S.InfoData>
                          <Calendar className="calendarIcon" />
                          <S.DataDescription>
                            <div>{register?.scheduleDate.replace(" ", " \u00A0")}</div>
                          </S.DataDescription>
                        </S.InfoData>
                      )}
                      {register?.lenghtOfStay && (
                        <S.LengthOfStayData>
                          <S.DataDescription>
                            <div>{`${convertTimeSpanToTime(register?.lenghtOfStay)}`}</div>
                          </S.DataDescription>
                        </S.LengthOfStayData>
                      )}
                    </S.TruckContainer>
                  ))
                ) : (
                  <div style={{ display: "flex", justifyContent: "center", width: "100%", alignItems: "center" }}>
                    <h1>{THERE_ARE_NO_RECORDS}</h1>
                  </div>
                )}
              </S.ItemsContainer>
            )}
          </div>
          {canScrollRight && (
            <S.ArrowButton onClick={scrollRight}>
              <RightOutlined style={{ fontSize: 14 }} />
            </S.ArrowButton>
          )}
        </S.ScrollWrapper>
      )}
    </S.CardContent>
  );
};

export default VehiclesCards;
